/**
 * Component: Semantic Search widget surface
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R11, SEARCH-SEMANTIC-V1-R03
 *
 * Embeddable Semantic Search surface. Wraps
 * ``POST /api/v1/search/semantic`` (vector-similarity search).
 *
 * AB#4403: brought to FRONTEND-WEB-V1 Semantic Search-page parity —
 * a "Semantic Search" heading + intro line; the query input with a
 * leading magnifier icon and the Search button inline on its right;
 * an empty-state ("Enter a query to search …") before any search
 * runs; the extractive answer (SEARCH-SEMANTIC-V1-R03 — the
 * ``answers[]`` passages Azure AI Search returns) rendered above the
 * result list; and a "Showing N results" line. The Batch-C "class
 * of recommendation" filter select was dropped — the portal page
 * doesn't carry it in this view, and the request no longer sends
 * ``filters.cor``.
 *
 * Risk Links: R7, R11
 * FMEA Links: FM7, FM11
 */

import React, { useCallback, useState } from 'react';

import type { WidgetApiClient } from '../api/client';
import { WidgetError } from '../api/errors';
import { trackWidgetUsage } from '../core/usage-tracking';
import { BrowseLink } from './browse-link';
import { SafeHtml } from './browse-content';
import {
  SURFACE_PRIMARY_BUTTON_STYLE,
  SURFACE_RESULTS_LIST_STYLE,
  SURFACE_RESULT_CARD_STYLE,
  SURFACE_WRAPPER_STYLE,
  SearchResultExpander,
  SurfaceErrorBanner,
  explainWidgetError,
  getMetaString,
  normalizeQuotes,
  pickResultSubtitle,
  pickResultTitle,
} from './shared';
import { SearchFilterPanel } from './search-filter-panel';
import {
  type Facet,
  type SearchFilters,
  INITIAL_FILTERS,
  toFilterRequest,
} from './search-filter-types';
import { useFilterAutoSearch } from './use-filter-auto-search';

/**
 * Trimmed Semantic Search response shape. Field names mirror the
 * backend ``SemanticSearchResponse`` / ``SearchResultResponse`` /
 * ``SearchAnswerResponse`` Pydantic models exactly so the widget
 * consumes the JSON without re-shaping. New fields are ignored at
 * runtime; promote them here as the widget starts consuming them.
 */
interface SemanticSearchResult {
  document_id: string;
  title: string;
  content: string;
  score: number;
  semantic_score?: number | null;
  keyword_score?: number | null;
  captions?: string[];
  highlights?: Record<string, string[]>;
  metadata?: Record<string, unknown>;
  source_url?: string | null;
}

/** SEARCH-SEMANTIC-V1-R03: extractive answer passage. */
interface SemanticAnswer {
  text: string;
  highlights?: string;
  score?: number;
  key?: string;
}

interface SemanticSearchResponse {
  results?: SemanticSearchResult[];
  total_count?: number | null;
  answers?: SemanticAnswer[];
  /** WIDGET-V1-R27: available index facets for the refine panel. */
  facets?: Facet[];
}

export interface SemanticSearchProps {
  client: WidgetApiClient;
  baseUrl: string;
  token: string;
  onError?: (error: Error) => void;
  /**
   * BROWSE-V1-R20: when true the result rows render the
   * cross-module BrowseLink (deployment-time flag, sourced from
   * widget config). Optional; defaults to false.
   */
  enableBrowseLinks?: boolean;
}

type Phase = 'idle' | 'loading' | 'done' | 'error';

// AB#4403 follow-up: the query input goes pale-blue once it holds a
// value, because the browser paints it as an autofill-selected field
// (`input:-internal-autofill-selected` — a UA `!important` rule that
// no `background` declaration can override). The only fix is the
// `:-webkit-autofill` box-shadow-inset trick, which needs a real
// stylesheet rule (you can't target the pseudo-class inline). Scoped
// to this surface's input so it can't leak onto the host page.
const SEMANTIC_INPUT_AUTOFILL_CSS = [
  '[data-galen-surface="semantic-search"] input:-webkit-autofill,',
  '[data-galen-surface="semantic-search"] input:-webkit-autofill:hover,',
  '[data-galen-surface="semantic-search"] input:-webkit-autofill:focus,',
  '[data-galen-surface="semantic-search"] input:-webkit-autofill:active{',
  '-webkit-box-shadow:0 0 0 1000px var(--galen-background,#ffffff) inset!important;',
  'box-shadow:0 0 0 1000px var(--galen-background,#ffffff) inset!important;',
  '-webkit-text-fill-color:var(--galen-foreground,#0f172a)!important;',
  'caret-color:var(--galen-foreground,#0f172a);',
  'transition:background-color 9999s ease-in-out 0s;}',
].join('');

function SearchGlyph(): React.ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

export const SemanticSearch: React.FC<SemanticSearchProps> = ({
  client,
  onError,
  enableBrowseLinks = false,
}) => {
  const [query, setQuery] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [response, setResponse] = useState<SemanticSearchResponse | null>(null);
  // WIDGET-V1-R27: server-side faceted filtering state.
  const [filters, setFilters] = useState<SearchFilters>(INITIAL_FILTERS);
  const [facets, setFacets] = useState<Facet[]>([]);
  // Bumped on a fresh query submit to collapse the refine panel.
  const [filterCollapseToken, setFilterCollapseToken] = useState(0);

  // WIDGET-V1-R27: the core search, re-runnable from both the form submit and
  // the debounced filter auto-search. Sends the user's refine selections for
  // server-side filtering and stores the returned facets for the panel.
  const runSearch = useCallback(async () => {
    if (query.trim().length === 0) return;

    setPhase('loading');
    setErrorMessage(null);

    // WIDGET-V1-R25: fire search_query on submit.
    void trackWidgetUsage(client, 'semantic-search', 'search_query', {
      query_text: query,
      mode: 'semantic',
    });

    try {
      const result = await client.postJson<SemanticSearchResponse>(
        '/api/v1/search/semantic',
        {
          query: normalizeQuotes(query),
          ...toFilterRequest(filters),
          prioritize_recent: filters.prioritizeRecent,
        },
      );
      setResponse(result);
      setFacets(result.facets ?? []);
      setPhase('done');
    } catch (e) {
      const err = e instanceof Error ? e : new WidgetError(String(e));
      setErrorMessage(explainWidgetError(err));
      setPhase('error');
      onError?.(err);
    }
  }, [client, query, filters, onError]);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (phase === 'loading') return;
      setFilterCollapseToken((n) => n + 1);
      void runSearch();
    },
    [phase, runSearch],
  );

  // WIDGET-V1-R27: re-run (debounced) when a refine selection changes, once a
  // search has produced a response.
  useFilterAutoSearch(filters, response !== null, () => void runSearch());

  const isBusy = phase === 'loading';
  const results = response?.results ?? [];
  const answers = response?.answers ?? [];
  const resultCount =
    typeof response?.total_count === 'number' ? response.total_count : results.length;

  return (
    <div data-galen-surface="semantic-search" style={SURFACE_WRAPPER_STYLE}>
      <div data-galen-semantic-search-header>
        <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700 }}>
          Semantic Search
        </h1>
        <p
          style={{
            margin: '0.25rem 0 0',
            fontSize: '0.9rem',
            color: 'var(--galen-muted, #475569)',
          }}
        >
          Find relevant guideline sections using natural language queries.
        </p>
      </div>

      {errorMessage !== null && <SurfaceErrorBanner message={errorMessage} />}

      <form
        onSubmit={(e) => void handleSubmit(e)}
        data-galen-semantic-search-form
        style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}
      >
        <style>{SEMANTIC_INPUT_AUTOFILL_CSS}</style>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            padding: '0 0.6rem',
            border: '1px solid var(--galen-border, #e2e8f0)',
            borderRadius: 'var(--galen-border-radius, 0.5rem)',
            background: 'var(--galen-background, #ffffff)',
            color: 'var(--galen-muted, #475569)',
          }}
        >
          <SearchGlyph />
          <input
            id="galen-semantic-query"
            data-galen-semantic-search-query
            type="search"
            aria-label="Semantic Search query"
            autoComplete="off"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={isBusy}
            placeholder="Ask a guideline related question…"
            style={{
              flex: 1,
              minWidth: 0,
              boxSizing: 'border-box',
              padding: '0.5rem 0',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: 'var(--galen-foreground, #0f172a)',
              fontFamily: 'inherit',
              fontSize: '0.95rem',
              // Defeat browser search-input chrome (the autofill
              // background workaround lives in
              // SEMANTIC_INPUT_AUTOFILL_CSS above).
              appearance: 'none',
              WebkitAppearance: 'none',
            }}
          />
        </div>
        <button
          type="submit"
          disabled={isBusy || query.trim().length === 0}
          data-galen-semantic-search-submit
          style={{
            ...SURFACE_PRIMARY_BUTTON_STYLE(isBusy || query.trim().length === 0),
            marginTop: 0,
            flexShrink: 0,
          }}
        >
          {isBusy ? 'Searching…' : 'Search'}
        </button>
      </form>

      {/* WIDGET-V1-R27: faceted refine panel (self-hides when nothing to refine) */}
      <SearchFilterPanel
        filters={filters}
        onFiltersChange={setFilters}
        onReset={() => setFilters(INITIAL_FILTERS)}
        facets={facets}
        resultCount={response !== null ? resultCount : undefined}
        collapseToken={filterCollapseToken}
      />

      {phase === 'idle' && (
        <div
          data-galen-semantic-search-empty
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '2.5rem 1rem',
            textAlign: 'center',
            color: 'var(--galen-muted, #475569)',
          }}
        >
          <span style={{ opacity: 0.7 }}>
            <svg
              viewBox="0 0 24 24"
              width="36"
              height="36"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </span>
          <p style={{ margin: 0, fontSize: '1rem', fontWeight: 500, color: 'var(--galen-foreground, #0f172a)' }}>
            Enter a query to search
          </p>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>
            Use natural language to find relevant clinical guideline sections.
          </p>
        </div>
      )}

      {phase === 'done' && response && (
        <div>
          <p
            data-galen-semantic-search-count
            style={{
              margin: '0 0 0.5rem',
              fontSize: '0.85rem',
              color: 'var(--galen-muted, #475569)',
            }}
          >
            {resultCount === 1 ? 'Showing 1 result' : `Showing ${resultCount} results`}
          </p>

          {/* SEARCH-SEMANTIC-V1-R03: extractive answer above the
              result list (the portal renders this too). */}
          {answers.length > 0 && (answers[0]?.highlights || answers[0]?.text) && (
            <div
              data-galen-semantic-search-answer
              role="region"
              aria-label="Semantic answer"
              style={{ marginBottom: '0.75rem', fontSize: '0.92rem', lineHeight: 1.45 }}
            >
              <SafeHtml html={answers[0]?.highlights || answers[0]?.text} />
            </div>
          )}

          <ul data-galen-semantic-search-results style={SURFACE_RESULTS_LIST_STYLE}>
            {results.map((result, index) => {
              const title = pickResultTitle(result);
              const subtitle = pickResultSubtitle(result);
              const corBadge =
                getMetaString(result.metadata, 'RecommendationStrength') ||
                getMetaString(result.metadata, 'cor');
              const loeBadge =
                getMetaString(result.metadata, 'EvidenceStrength') ||
                getMetaString(result.metadata, 'loe');
              const guidelineId =
                result.metadata?.['Guidelineid'] ??
                result.metadata?.['GuidelineId'] ??
                null;
              const guidelineSectionIdRaw = result.metadata?.['GuidelineSectionId'];
              const sectionId =
                guidelineSectionIdRaw != null && guidelineSectionIdRaw !== 'null'
                  ? (guidelineSectionIdRaw as number | string)
                  : null;
              const guidelineName = getMetaString(result.metadata, 'GuidelineName');
              const sectionName = getMetaString(
                result.metadata,
                'GuidelineSectionName',
              );
              return (
                <li
                  key={result.document_id}
                  data-galen-search-result-card
                  style={SURFACE_RESULT_CARD_STYLE}
                >
                  <div style={{ fontWeight: 600 }}>
                    {result.source_url ? (
                      <a href={result.source_url} target="_blank" rel="noreferrer noopener">
                        {title}
                      </a>
                    ) : (
                      title
                    )}
                  </div>
                  {subtitle && (
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: 'var(--galen-muted, #475569)',
                        marginTop: '0.125rem',
                      }}
                    >
                      {subtitle}
                    </div>
                  )}
                  {(corBadge || loeBadge) && (
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: 'var(--galen-muted, #475569)',
                        marginTop: '0.25rem',
                      }}
                    >
                      {corBadge && <span>CoR {corBadge}</span>}
                      {corBadge && loeBadge && ' · '}
                      {loeBadge && <span>LoE {loeBadge}</span>}
                    </div>
                  )}
                  <SearchResultExpander
                    result={result}
                    surfaceKey="semantic-search"
                    client={client}
                    query={query}
                    footerRight={
                      <BrowseLink
                        enabled={enableBrowseLinks}
                        guidelineId={guidelineId as number | string | null}
                        sectionId={sectionId}
                        client={client}
                        surfaceKey="semantic-search"
                        documentId={result.document_id}
                        rankingPosition={index + 1}
                        guidelineName={guidelineName || undefined}
                        sectionName={sectionName || undefined}
                        query={query}
                      />
                    }
                  />
                </li>
              );
            })}
            {results.length === 0 && (
              <li style={{ fontSize: '0.9rem', color: 'var(--galen-muted, #475569)' }}>
                No results.
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
